"use client"

import { postChangeLanguageOrShipping } from "@/actions/selection/client/postChangeLanguageOrShipping"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import { type CountryCode } from "@/data/country-data"
import { resolveApp } from "@/i18n/apps"
import { removeLeadingSlashPattern } from "@/lib/siteconfig/regexPatterns"
import { isCurrentCentraMarket } from "@/src/utils/centra/isCurrentCentraMarket"
import { clsx } from "@/src/utils/clsx"
import { useAppForCurrentLocale } from "@/src/utils/locale/useAppForCurrentLocale"
import Link from "next/link"
import toast from "react-hot-toast"

type CountryLanguageLinkProps = {
    href: string
    path: string
    countryCode: CountryCode
    label: string
    language?: string | null
    className?: string
}

export function CountryLink({
    href,
    path,
    countryCode,
    label,
    className,
    language
}: CountryLanguageLinkProps) {
    const { countryCode: currentCountryCode } = useAppForCurrentLocale()
    const sessionCountry = currentCountryCode?.toLowerCase()
    const handleClick = async (e: React.MouseEvent<HTMLElement>) => {
        if (isCurrentCentraMarket(path, countryCode, sessionCountry)) return

        e.preventDefault()

        const app = resolveApp(path.replace(removeLeadingSlashPattern, ""))

        const result = await postChangeLanguageOrShipping({
            countryCode,
            languageCode: language ?? app.language,
            preferredApp: app.path
        })

        if (!result.success) {
            toast.error("Failed to change shipping destination. Please try again.")
            return
        }

        window.location.href = path
    }

    return (
        <Link
            href={href}
            className={clsx(
                "leading-160 font-trimRegular group flex items-center gap-5 pb-8 text-16 font-400 uppercase -tracking-[0.02em]",
                className
            )}
            prefetch={false}
            onClick={handleClick}
        >
            <span className="md:transition-colors md:duration-300 md:group-hover:text-shades-80-white">
                {label}
            </span>
            <IconArrowRight className="ml-2 h-12 w-12 opacity-0 group-hover:text-shades-80-white md:h-16 md:w-16 md:translate-x-[-10px] md:transition-transform md:duration-300 md:group-hover:translate-x-0 md:group-hover:opacity-100" />
        </Link>
    )
}
